import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { authApi } from '@/api/client'
import type { GetRiderLocations200ResponseInner } from '@/openapi'
import mqtt from '@/sync/mqtt'

import { useTabsStore } from './tabs'

const baseURL = (import.meta.env.VITE_API_BASE_URL as string)?.replace(/\/$/, '') ?? ''

export const useAuthStore = defineStore(
  'auth',
  () => {
    const accessToken = ref<string | null>(null)
    const selectedLocationId = ref<string | null>(null)
    const selectedOrganizationId = ref<string | null>(null)

    const isAuthenticated = computed(() => !!accessToken.value)

    async function login(loginData: { email: string; password: string }) {
      const response = await authApi.login({ loginRequest: loginData })
      accessToken.value = response.accessToken
    }

    async function supportLogin({
      accessToken: token,
      organizationId,
      locationId
    }: {
      accessToken: string
      organizationId: string
      locationId: string
    }) {
      const response = await fetch(`${baseURL}/users/token/${token}/checkSupport`)
      if (!response.ok) return false
      const isSupport = (await response.json()) as boolean
      if (!isSupport) return false
      accessToken.value = token
      selectedOrganizationId.value = organizationId
      selectedLocationId.value = locationId
      return true
    }

    async function logout() {
      mqtt.unsubscribe()
      accessToken.value = null
      selectedLocationId.value = null
      selectedOrganizationId.value = null
    }

    async function selectLocation(location: GetRiderLocations200ResponseInner) {
      selectedLocationId.value = location.id
      mqtt.subscribe(location.id)
      const tabsStore = useTabsStore()
      await tabsStore.refreshTabs()
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('storage', e => {
        if (e.key === 'accessToken' && !localStorage.getItem('accessToken')) {
          logout()
        }
      })
    }

    return {
      accessToken,
      selectedLocationId,
      selectedOrganizationId,
      isAuthenticated,

      login,
      supportLogin,
      logout,
      selectLocation
    }
  },
  {
    persist: true
  }
)
